import { TextThreadEditorVMConfig } from 'components/editors/thread/text/textThread';
import { wccModules } from 'enums/wccModules';
import { DOMHelpers } from 'helpers/dom';
import { getStatusText } from 'helpers/people';
import { inject, injectable } from 'inversify';
import { Observable, ObservableArray, PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import threadEditingOverlay from 'managers/threadEditingOverlay';
import { TopicManager } from 'managers/topic';
import { EffectsContainer } from 'mixins/withEffects';
import { Person } from 'models/person';
import { Topic } from 'models/topic';
import { TopicThread } from 'models/topicThread';
import { PersonAvatarVMConfig } from '../../avatar/person/person';
import './comment.html';
import './comment.scss';
import CommentFooterVM, { CommentFooterVMConfig } from './footer';
import CommentToDoManager from './managers/todo';
import CommentRequestButtonVM from './requestButton';

components
    .registerLocal('comment-abuse-cover', () => require('./abuseCover'))
    .registerLocal('comment-body', () => require('./body'))
    .registerLocal('comment-content', () => require('./content'))
    .registerLocal('comment-clear-abuse-report-button', () => require('./clearAbuseReportButton'))
    .registerLocal('comment-creator-cover', () => require('./creatorCover'))
    .registerLocal('comment-done-cover', () => require('./doneCover'))
    .registerLocal('comment-edit-button', () => require('./editButton'))
    .registerLocal('comment-footer', () => require('./footer'))
    .registerLocal('comment-ideation-approval-controls', () => require('./ideationApprovalControls'))
    .registerLocal('comment-ideation-votes-bubbles', () => require('./ideationVotesBubbles'))
    .registerLocal('comment-ideation-voter', () => require('./ideationVoter'))
    .registerLocal('comment-mark-as-done-button', () => require('./markAsDoneButton'))
    .registerLocal('comment-owner-tags', () => require('./ownerTags'))
    .registerLocal('comment-participants', () => require('./participants'))
    .registerLocal('comment-pin-button', () => require('./pinButton'))
    .registerLocal('comment-refresh-translation-button', () => require('./refreshTranslationButton'))
    .registerLocal('comment-remove-button', () => require('./removeButton'))
    .registerLocal('comment-removed-cover', () => require('./removedCover'))
    .registerLocal('comment-replies', () => require('./replies'))
    .registerLocal('comment-reply', () => require('./reply'))
    .registerLocal('comment-report-abuse-button', () => require('./reportAbuseButton'))
    .registerLocal('comment-response-setting', () => require('./responseSetting'))
    .registerLocal('comment-settings', () => require('./settings'))
    .registerLocal('comment-tags', () => require('./tags'))
    .registerLocal('comment-video-tools', () => require('./videoTools'))
    .registerLocal('comment-visibility-tags', () => require('./visibilityTags'))
    .registerLocal('comment-visible-to', () => require('./visibleTo'))
    .preload('comment-editor-inline', 'reply-editor-standalone', 'reply-suggestion', 'open-ai-completion', 'person-avatar');

const { discussionId, now } = settings;

export type CommentVMTheme = 'main' | 'compact';
export type CommentVMHeader = 'creator' | 'title';

export interface CommentVMConfig {
    comment?: SubscribableOrNullableValue<TopicThread>
    todoMode?: SubscribableOrNullableValue<boolean>
    pinMode?: SubscribableOrNullableValue<boolean>
    disableStyling: SubscribableOrNullableValue<boolean>
    hideControls?: SubscribableOrNullableValue<boolean>
    onlyContent?: SubscribableOrNullableValue<boolean>
    maxHeight?: SubscribableOrNullableValue<number>
    theme?: SubscribableOrNullableValue<CommentVMTheme>
    header?: SubscribableOrNullableValue<CommentVMHeader>
}

@injectable()
export default class CommentVM {
    private isRegularComment: Subscribable<boolean>
    private isRefreshingTranslation = ko.observable(false);

    labels = labels;
    messages = messages;

    ribbonLanguageClass = `wcc-ribbon--${settings.languageCode}`

    commentEditorConfig: TextThreadEditorVMConfig
    avatarConfig: PersonAvatarVMConfig

    footerConfig: CommentFooterVMConfig
    footer: Observable<CommentFooterVM | undefined> = ko.observable();
    replyEditor = this.footer.pluck(f => f.replyEditor);
    _replyEditorActive = this.replyEditor.pluck(e => e.isActive, false);
    replyEditorActive = ko.pureComputed(() => this._replyEditorActive() 
        && this.replyEditor()?.oReplaySuggestionRef()?.openAiCompletionRef()?.cGenerationViewSelected() != true);

    buttons: ObservableArray<CommentRequestButtonVM> = ko.observableArray();

    avatarNode = ko.observable<HTMLElement>();
    titleNode = ko.observable<HTMLElement>();
    
    comment: Subscribable<TopicThread | undefined>
    id: Subscribable<string | undefined>
    topicId: Subscribable<string | undefined>
    title: Subscribable<string | undefined>
    creatorName: Subscribable<string | undefined>
    createDateStr: Subscribable<string | undefined>
    status: Subscribable<string | undefined>
    
    hasInitialReplies: Subscribable<boolean>

    maxHeight: Subscribable<number | undefined>

    creatorHeaderShown: Subscribable<boolean>
    titleHeaderShown: Subscribable<boolean>
    footerShown: Subscribable<boolean>
    ideationVoterShown: Subscribable<boolean>
    creatorCoverShown: Subscribable<boolean>
    hideControls: Subscribable<boolean>
    ideationVotesBubblesShown: Subscribable<boolean>
    onlyContent: Subscribable<boolean>

    busy = ko.pureComputed(() => this.checkIfBusy());    

    todoMode: Subscribable<boolean>

    editMode: Observable<boolean>
    readMode: Subscribable<boolean>
    
    isPrivate: Subscribable<boolean>
    isAbuse: Subscribable<boolean>
    isResponseMandatory: Subscribable<boolean>
    isInToDoList: Subscribable<boolean>
    isIdeation: Subscribable<boolean>
    isEditing: Subscribable<boolean>
    isReady: Subscribable<boolean>
    
    constructor(
        @inject(wccModules.componentConfig) config: CommentVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const theme = ko.strictComputed(name => this.getTheme(name), [config.theme]);

        this.todoMode = ko.flattenComputed(config.todoMode, false);
        this.editMode = ko.observable(false).extend({ deferred: true });
        this.readMode = this.editMode.not();
        const pinMode = ko.flattenComputed(config.pinMode, false);
        const disabledStylingMode = ko.flattenComputed(config.disableStyling, false);
        const disableStyling = ko.pureComputed(() => pinMode() || disabledStylingMode());

        this.comment = ko.flattenComputed(config.comment);
        this.id = this.comment.pluck(c => c.id);
        this.topicId = this.comment.pluck(c => c.topicId);
        this.title = this.comment.pluck(c => c.title);
        const createDate = this.comment.pluck(c => c.createDate);        
        this.createDateStr = createDate.mapNotNull(date => moment(date).locale(settings.languageCode).from(now()));     
        this.isEditing = this.comment.pluck(c => c.editing, false);
        this.isResponseMandatory = this.comment.pluck(c => c.isResponseMandatory, false);     
        const spamComplaintCount = this.comment.pluck(c => c.spamComplaintCount, 0);
        this.isAbuse = ko.pureComputed(() => spamComplaintCount() > 0 && isModeratorUser());
        this.isPrivate = this.comment.pluck(c => c.isWhisper() || c.isForSelectedParticipants() || c.isForTaggedPeople(), false);
        const isPinned = this.comment.pluck(c => c.isPinned, false);

        const initialRepliesCount = this.comment.pluck(c => c.repliesCount, 0);
        this.hasInitialReplies = initialRepliesCount.pluck(c => c > 0);

        this.maxHeight = ko.strictComputed(target => {
            if (target != undefined && target > 0)
                return target;
        }, [config.maxHeight]);

        const headerType = ko.flattenComputed(config.header, 'creator');
        this.creatorHeaderShown = headerType.is('creator');
        this.titleHeaderShown = headerType.is('title');
        
        const isDeleted = this.comment.pluck(c => c.isDeleted, false);

        const contentOnlyMode = ko.flattenComputed(config.onlyContent, false);
        this.onlyContent = ko.pureComputed(() => pinMode() || contentOnlyMode());        

        const currentUserManager = storage.get(CurrentUserManager, { discussionId });
        const user = currentUserManager.pluck('person');
        const isModeratorUser = user.pluck(u => u.isModerator, false);

        const participant = this.comment.mapNotNull(comment => Person.fromThread(comment));
        this.creatorName = participant.pluck(p => p.nicknameOrName);
        this.status = participant.mapNotNull(getStatusText);
        const isModeratorComment = participant.pluck(p => p.isModerator, false);
        const isObserverComment = participant.pluck(p => p.isObserver, false);
        this.isRegularComment = participant.pluck(p => p.isRegular, false);
        
        const topicManager = storage.get(TopicManager, { topicId: this.topicId });
        const topic = topicManager.pluck('topic');
        this.isIdeation = topic.pluck(t => t.isIdeation, false);

        const ideationManager = storage.get(IdeationManager, { topicId: this.topicId });
        const isIdeationRankingStage = ideationManager.pluck(m => m.isRatingStage, false);
        const isIdeationVotingStage = ideationManager.pluck(m => m.isVotingStage, false);

        const hiddenControlsMode = ko.flattenComputed(config.hideControls, false);
        this.hideControls = ko.pureComputed(() => pinMode() || hiddenControlsMode() || isDeleted());

        this.ideationVotesBubblesShown = ko.pureComputed(() => isIdeationRankingStage() && !isIdeationVotingStage());

        this.ideationVoterShown = isIdeationVotingStage;
        this.footerShown = this.ideationVoterShown.not();

        const todoManager = new CommentToDoManager(this.comment, storage);
        const isInToDoListOrReported = todoManager.isInToDoListOrReported;
        this.isInToDoList = todoManager.isInToDoList;

        const isHeaderFieldsHovered = ko.observable(false);
        this.creatorCoverShown = ko.pureComputed(() => isHeaderFieldsHovered());

        this.commentEditorConfig = { thread: this.comment, focus: true }
        this.avatarConfig = { person: participant }

        this.footerConfig = {
            comment: this.comment,
            buttons: this.buttons,
            pinMode,
            onlyContent: this.onlyContent,
            hideControls: this.hideControls,
            busy: this.busy
        }

        this.isReady = ko.pureComputed(() => {
            return this.comment() != undefined &&
                participant() != undefined &&
                user() != undefined &&
                topic() != undefined;
        });

        effects.register((topic, comment, isEditing) => {
            this.editMode(false);

            if (topic != undefined && comment != undefined && isEditing) {
                const shouldShowInlineEditor = this.checkIfShouldShowInlineEditor(topic);

                if (shouldShowInlineEditor)
                    this.editMode(true);
                else
                    threadEditingOverlay.edit(comment);
            }
        }, [topic, this.comment, this.isEditing]);

        effects.register((isModerator, node) => {
            if (isModerator && node != undefined)
                return [
                    DOMHelpers.onNodeEvent(node, 'mouseenter', () => isHeaderFieldsHovered(true)),
                    DOMHelpers.onNodeEvent(node, 'mouseleave', () => isHeaderFieldsHovered(false))
                ]
        }, [isModeratorUser, this.titleNode]);

        ko.applyBindingsToNode(info.element, {
            'visible': this.isReady,

            'css': {
                'topic__comment': true,
                'topic__comment--moderator': ko.pureComputed(() => !disableStyling() && !this.isIdeation() && isModeratorComment()),
                'topic__comment--observer': ko.pureComputed(() => !disableStyling() && isObserverComment()),
                'topic__comment--private': ko.pureComputed(() => !disableStyling() && this.isPrivate()),
                'topic__comment--todo': ko.pureComputed(() => !disableStyling() && isInToDoListOrReported()),
                'topic__comment--abuse': ko.pureComputed(() => !disableStyling() && this.isAbuse()),
                'topic__comment--response-mandatory': ko.pureComputed(() => !disableStyling() && this.isResponseMandatory()),
                'topic__comment--limited': this.maxHeight.isNotNull(),
                'topic__comment--pinned': isPinned
            }
        });

        ko.applyBindingsToNode(info.element, {
            'css': theme
        }, this);
    }

    private checkIfBusy() {
        return this.isRefreshingTranslation() || this.buttons().some(b => b.isBusy());
    }

    private checkIfShouldShowInlineEditor(topic: Topic) {
        if (topic.isSurvey() || topic.isCardSort() || topic.isCardScore() || topic.isVideoPresentation() || topic.isPinBoard())
            return !this.isRegularComment();

        return true;
    }   

    private getTheme(name?: CommentVMTheme) {
        switch (name) {
            case 'compact': return 'topic__comment--compact';
        }
    }        
}